import React from 'react';

/**
 * TWO LIVE REGIONS THAT ARE ALWAYS MOUNTED.
 *
 * An assistive technology announces a CHANGE inside a live region it is
 * already observing. A region that appears in the same DOM commit as its own
 * first text is not being observed yet, so nothing is announced -- which is
 * exactly how the outcome and refusal panels were built: they returned `null`
 * until an answer arrived, then mounted `role="status"` and the answer
 * together. A sighted user saw the result; a screen-reader user was told
 * nothing had happened.
 *
 * So the regions mount with the surface, empty, and only their TEXT changes.
 *
 * There are two of them rather than one node whose `role`/`aria-live` is
 * swapped between polite and assertive. Changing the politeness of a region a
 * screen reader is already observing is unreliable across ATs -- some cache
 * the setting at first observation. Two persistent regions, one of which is
 * empty at any moment, needs nothing to be re-observed.
 *
 * `urgent` is for a settled bad answer -- FAILED, REFUSED, DENIED, an
 * unreadable outcome, or a refusal. Those interrupt. A hold or an execution
 * confirmation waits its turn.
 */
export const AuthorityAnnouncer = ({ message, urgent = false }) => {
  const text = message || '';
  return (
    <>
      <div
        data-testid="authority-announce-polite"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {urgent ? '' : text}
      </div>
      <div
        data-testid="authority-announce-alert"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {urgent ? text : ''}
      </div>
    </>
  );
};

export default AuthorityAnnouncer;
